import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// DOES MERGE BEHAVIOR WORK
function DetailRow({ label, value }) {
  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="body2">
        <strong>{label}:</strong> {value ?? '—'}
      </Typography>
    </Box>
  );
}

function MetadataBlock({ metadata }) {
  const hasMetadata =
    metadata &&
    typeof metadata === 'object' &&
    !Array.isArray(metadata) &&
    Object.keys(metadata).length > 0;

  if (!hasMetadata) {
    return <Typography variant="body2">No metadata</Typography>;
  }

  return (
    <Box
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.default',
      }}
    >
      <Typography
        variant="body2"
        component="pre"
        sx={{
          m: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'monospace',
        }}
      >
        {JSON.stringify(metadata, null, 2)}
      </Typography>
    </Box>
  );
}

function getConnectionStatus(gateway) {
  return String(gateway?.iot_connection?.status || 'unknown').toLowerCase();
}

function getStatusColor(status) {
  const normalized = String(status || '').toLowerCase();

  if (normalized === 'connected') return 'success';
  if (normalized === 'disconnected') return 'warning';
  if (normalized === 'error') return 'error';
  return 'default';
}

export default function AmxGatewayView({ data }) {
  const gateways = Array.isArray(data?.gateways) ? data.gateways : [];

  if (gateways.length === 0) {
    return <Typography>No gateways found for this account context.</Typography>;
  }

  return (
    <Stack spacing={2}>
      {gateways.map((gateway, index) => (
        <Card
          key={gateway.gateway_id || index}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="h6">
                  {gateway.display_name || gateway.gateway_code || `Gateway ${index + 1}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {gateway.gateway_code || 'No gateway code'}
                </Typography>
              </Box>

              <Chip
                label={getConnectionStatus(gateway)}
                color={getStatusColor(getConnectionStatus(gateway))}
                size="small"
              />
            </Stack>

            <DetailRow label="Partner ID" value={gateway.partner_id} />
            <DetailRow label="Gateway ID" value={gateway.gateway_id} />
            <DetailRow label="Gateway Code" value={gateway.gateway_code} />
            <DetailRow label="Display Name" value={gateway.display_name} />
            <DetailRow label="IoT Thing Name" value={gateway.iot_thing_name} />
            <DetailRow label="IoT Thing ID" value={gateway.iot_thing_id} />
            <DetailRow label="IoT Thing ARN" value={gateway.iot_thing_arn} />
            <DetailRow label="IoT Certificate ID" value={gateway.iot_cert_id} />
            <DetailRow label="IoT Certificate ARN" value={gateway.iot_cert_arn} />
            <DetailRow label="IoT Group Name" value={gateway.iot_group_name} />
            <DetailRow label="IoT Group ID" value={gateway.iot_group_id} />
            <DetailRow label="IoT Group ARN" value={gateway.iot_group_arn} />
            <DetailRow label="IoT Policy Name" value={gateway.iot_policy_name} />
            <DetailRow label="IoT Connection Status" value={gateway.iot_connection?.status} />
            <DetailRow
              label="IoT Connection Timestamp"
              value={gateway.iot_connection?.timestamp}
            />
            <DetailRow
              label="IoT Disconnect Reason"
              value={gateway.iot_connection?.disconnect_reason}
            />
            <DetailRow label="Created At" value={gateway.created_at} />
            <DetailRow label="Updated At" value={gateway.updated_at} />

            <Divider sx={{ my: 2 }} />

            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>Metadata</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <MetadataBlock metadata={gateway.metadata} />
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}
